import { createI18n, type LocaleMessageDictionary } from 'vue-i18n';

type LocaleMessages = LocaleMessageDictionary<string>;

const modules = import.meta.glob<LocaleMessages>('./languages/*.json', { import: 'default' });
const pendingLocaleLoads = new Map<Locale, Promise<LocaleMessages>>();

export const SUPPORTED = [
  'cs',
  'da',
  'de',
  'en',
  'es',
  'fr',
  'it',
  'ja',
  'nl',
  'pl',
  'ru',
  'zh',
] as const;
type Locale = (typeof SUPPORTED)[number];

const DEFAULT_LOCALE: Locale = 'de';

export function isSupportedLocale(loc?: string): loc is Locale {
  return !!loc && (SUPPORTED as readonly string[]).includes(loc);
}

function norm(raw?: string): Locale {
  const s = (raw || DEFAULT_LOCALE).toLowerCase().slice(0, 2);
  return isSupportedLocale(s) ? s : DEFAULT_LOCALE;
}

/**
 * Creates a new i18n instance without preloaded messages (loaded on demand).
 */
export function createI18nInstance(initial?: string) {
  return createI18n({
    legacy: false,
    globalInjection: true,
    locale: norm(initial),
    fallbackLocale: DEFAULT_LOCALE,
    messages: {},
  });
}

/**
 * Loads locale messages on demand.
 */
export async function loadLocale(i18n: ReturnType<typeof createI18nInstance>, loc?: string) {
  const l = norm(loc);
  const hasLocale = i18n.global.availableLocales.includes(l);

  if (!hasLocale) {
    const key = `./languages/${l}.json`;
    const loader = modules[key];
    if (loader) {
      const pending =
        pendingLocaleLoads.get(l) ||
        loader().then((msgs) => {
          i18n.global.setLocaleMessage(l, msgs);
          return msgs;
        });
      pendingLocaleLoads.set(l, pending);
      await pending;
      pendingLocaleLoads.delete(l);
    }
  }

  const { locale } = i18n.global;
  locale.value = l;
}
